import { z } from 'zod';
import type { PropertyResponse } from '@/shared/api';

export const propertySchema = z.object({
  name: z.string().min(2, 'Mindestens 2 Zeichen').max(200),
  operationalName: z.string().max(80).optional().or(z.literal('')),
  slug: z
    .string()
    .min(2, 'Mindestens 2 Zeichen')
    .max(80)
    .regex(/^[a-z0-9-]+$/, 'Nur Kleinbuchstaben, Ziffern, Bindestriche'),
  description: z.string().max(4000).optional().or(z.literal('')),
  propertyType: z.enum(['Apartment', 'House', 'Villa', 'Studio']),
  timezone: z.string().min(1).max(64),
  addressQuery: z.string().max(300).optional(),
  streetName: z.string().min(2, 'Straße fehlt').max(200),
  // Limit identisch zu FluentValidation + DB (20); internationale Nummern wie 1a / 1 A / 12-14 bleiben gültig (E3).
  houseNumber: z.string().min(1, 'Hausnummer fehlt').max(20),
  city: z.string().min(2).max(120),
  postalCode: z.string().min(2).max(20),
  countryCode: z.string().length(2, 'ISO-2 Country-Code (z. B. DE)'),
  latitude: z.number().nullable().optional(),
  longitude: z.number().nullable().optional(),
  needsAddressReview: z.boolean().default(true),
  occupancyAdults: z.coerce.number().int().min(1).max(30),
  occupancyChildren: z.coerce.number().int().min(0).max(30),
  bedrooms: z.coerce.number().int().min(0).max(20),
  bathrooms: z.coerce.number().min(0).max(20),
  currency: z.string().length(3, 'ISO-Currency (z. B. EUR)'),
  cleaningFee: z.coerce.number().min(0).max(10_000),
  baseNightlyRate: z.coerce.number().min(0).max(100_000),
  autoProvisionToChannex: z.boolean().default(true),
});

export type PropertyValues = z.infer<typeof propertySchema>;

export function toFormDefaults(p?: PropertyResponse): Partial<PropertyValues> {
  if (!p) {
    return {
      name: '',
      operationalName: '',
      slug: '',
      description: '',
      propertyType: 'Apartment',
      timezone: 'Europe/Berlin',
      addressQuery: '',
      streetName: '',
      houseNumber: '',
      city: '',
      postalCode: '',
      countryCode: 'DE',
      needsAddressReview: true,
      occupancyAdults: 2,
      occupancyChildren: 0,
      bedrooms: 1,
      bathrooms: 1,
      currency: 'EUR',
      cleaningFee: 50,
      baseNightlyRate: 100,
      autoProvisionToChannex: true,
    };
  }
  return {
    name: p.name,
    operationalName: p.operationalName ?? '',
    slug: p.slug,
    description: p.description ?? '',
    propertyType: p.propertyType,
    timezone: p.timezone,
    addressQuery: p.address.street,
    streetName: p.address.streetName,
    houseNumber: p.address.houseNumber,
    city: p.address.city,
    postalCode: p.address.postalCode,
    countryCode: p.address.countryCode,
    latitude: p.address.latitude,
    longitude: p.address.longitude,
    needsAddressReview: false,
    occupancyAdults: p.occupancyAdults,
    occupancyChildren: p.occupancyChildren,
    bedrooms: p.bedrooms,
    bathrooms: p.bathrooms,
    currency: p.currency,
    cleaningFee: p.cleaningFee,
    baseNightlyRate: p.baseNightlyRate,
    autoProvisionToChannex: false,
  };
}
